import { createVariantChangeEvent, publish, PUB_SUB_EVENTS } from '@/scripts/core/global'
import { ModalDialog } from '@/scripts/theme/modal-dialog'
import {
	replaceAll,
} from '@/scripts/core/global'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import { type ProductForm } from '@/scripts/product/product-form'
import { type MediaGallery } from '@/scripts/product/media-gallery'
import { type ShareButton } from '@/scripts/optional/share-button'
import { type PickupAvailability } from '@/scripts/optional/pickup-availability'
import { type ProductModal } from '@/scripts/product/product-modal'
import { UcoastEl } from '@/scripts/core/UcoastEl';
import { type ProductVariant } from '@/scripts/shopify'
import { type VariantRadios } from '@/scripts/theme/variant-radios'
import { type ProductInfo } from '@/scripts/product/product-info'
import { ATTRIBUTES, SELECTORS } from '@/scripts/core/global';
import { MAIN_PRODUCT_SECTION_ID } from '@/scripts/core/global'

export class VariantSelects extends UcoastEl {
	static htmlSelector = 'variant-selects'
	static selectors = {

	}
	instanceSelectors = VariantSelects.selectors
	currentVariant?: ProductVariant
	options?: string[]
	variantData?: ProductVariant[]
	constructor() {
		super()
		this.setInstanceSelectors()
		this.addEventListener('change', (event) => {
			this.onVariantChange(event).catch(console.error)
		})

		q.ol('.swatch-link', this)?.forEach(swatch => {
            swatch.addEventListener('click', this.onClickSwatch.bind(this))
        })

		q.ol('.color-dropdown', this)?.forEach(swatch => {
            swatch.addEventListener('change', this.onChangeColorDropdown.bind(this))
        })

		q.ol('.color-swatch-tab', this)?.forEach(swatchTab => {
			swatchTab.addEventListener('click', this.onClickSwatchTab.bind(this))
		})

		this.reorderBuildset()
	}

	reorderBuildset() {
		const swatchField = q.rs('fieldset[data-product-option="color"]', document)
		const buildSet = q.rs('.build-set-block', document)
		if(!swatchField || !buildSet) return
		swatchField.appendChild(buildSet)
	}

	override async connectedCallback(): Promise<void> {
		// Wait for variant data to be available before processing variant change
		// This ensures the button state is correctly set after color switches
		try {
			this.getVariantData()
		} catch (error) {
			// Variant data not ready yet, wait a tick and try again
			await new Promise(resolve => setTimeout(resolve, 0))
			try {
				this.getVariantData()
			} catch (e) {
				console.warn('Variant data not available on connectedCallback', e)
				return
			}
		}
		try {
			await this.onVariantChange()
		} catch (error) {
			console.error('Error in onVariantChange during connectedCallback:', error)
			// Ensure button state is updated even if there's an error
			if (this.currentVariant) {
				this.toggleAddButton(false, '')
			}
		}
	}

	setInstanceSelectors() {
		this.instanceSelectors = VariantSelects.selectors
	}

	async onVariantChange(event?: Event) {
		if(event) {
			const target = q.rt<Event, HTMLSelectElement>(event)
			if(target.classList.contains('disable-variant-change')) return
		}
		
		this.updateOptions()
		this.updateMasterId()
		this.toggleAddButton(true, '', false)
		this.updatePickupAvailability()
		this.removeErrorMessage()
		this.updateVariantStatuses()

		if (!this.currentVariant) {
			this.toggleAddButton(true, '', true)
			this.setUnavailable()
		} else {
			this.updateMedia()
			this.updateURL()
			this.updateVariantInput()
			try {
				await this.renderProductInfo()
			} catch (error) {
				console.error('Error in renderProductInfo:', error)
				// If renderProductInfo fails, still try to enable button if variant is available
				if (this.currentVariant?.available) {
					this.toggleAddButton(false, '')
				}
			}
			this.updateShareUrl()
		}
	}

	updateOptions() {
		this.options = Array.from(
			q.rl<HTMLSelectElement>('select', this),
			(select) => select.value
		)
	}

	updateMasterId() {
		this.currentVariant = this.getVariantData().find((variant) => {
			return !variant.options
				.map((option, index) => {
					return this.options && this.options[index] === option
				})
				.includes(false)
		})
	}

	updateMedia() {
		const currentVariant = this.currentVariant
		if (!currentVariant) return
		if (!currentVariant.featured_media) return

		const mediaGalleries = q.ol<MediaGallery>(
			`[id^="MediaGallery-${this.dataset.section}"]`
		)
		mediaGalleries?.forEach((mediaGallery) =>
			mediaGallery.setActiveMedia(
				`${this.dataset.section}-${currentVariant.featured_media.id}`,
				true
			)
		)

		const modalContent = q.os(
			`#ProductModal-${this.dataset.section} .product-media-modal__content`
		)
		if (!modalContent) return
		const newMediaModal = q.rs(
			`[data-media-id="${currentVariant.featured_media.id}"]`,
			modalContent
		)
		modalContent.prepend(newMediaModal)
	}

	updateURL() {
		if (!this.currentVariant || this.dataset.updateUrl === 'false') return
		window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`)
	}

	updateShareUrl() {
		if (!this.currentVariant) throw new Error('cannot updateShareUrl without currentVariant')
		const shareButton = q.os<ShareButton>(`#Share-${this.dataset.section}`)
		if (!shareButton || !shareButton.updateUrl) return
		shareButton.updateUrl(
			`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`
		)
	}

	updateVariantInput() {
		const currentVariant = this.currentVariant
		if (!currentVariant) throw new Error('cannot updateVariantInput without currentVariant')
		const productForms = q.ol(
			`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
		)
		productForms?.forEach((productForm) => {
			// Try input first, then select (for noscript fallback)
			const input = q.os<HTMLInputElement>('input[name="id"]', productForm)
			const select = q.os<HTMLSelectElement>('select[name="id"]', productForm)
			
			if (input) {
				input.value = `${currentVariant.id}`
				input.dispatchEvent(new Event('change', { bubbles: true }))
			} else if (select) {
				select.value = `${currentVariant.id}`
				select.dispatchEvent(new Event('change', { bubbles: true }))
			} else {
				console.warn(`No variant ID input/select found in form for section ${this.dataset.section}`)
			}

			productForm.querySelectorAll('wishlist-button-product').forEach((wishlistButton) => {
				wishlistButton.dataset.variantId = `${currentVariant.id}`
			})
		})
	}

	getVariantOptionFromIndex(variant: ProductVariant, index: number) {
		switch (index) {
			case 1:
				return variant.option1
			case 2:
				return variant.option2
			case 3:
				return variant.option3
			default:
				throw new Error('invalid index')
		}
	}

	updateVariantStatuses() {
		if (!this.variantData) throw new Error('cannot updateVariantStatuses without variantData')
		const selectedOptionOneVariants = this.variantData.filter(
			(variant) =>
				q.rs<HTMLInputElement | HTMLOptionElement>(':checked', this).value ===
				variant.option1
		)
		const inputWrapperNodes = q.ol('.product-form__input', this)
		const inputWrappers = inputWrapperNodes?.length ? Array.from(inputWrapperNodes) : []
		if (inputWrappers.length) {
			inputWrappers.forEach((option: HTMLElement, index: number) => {
				if (index === 0) return
				const optionInputNodes = q.ol<HTMLInputElement | HTMLOptionElement>(
					'input[type="radio"], option',
					option
				)
				const optionInputs = optionInputNodes?.length ? Array.from(optionInputNodes) : []

				const previousOptionSelected = q.rs<HTMLInputElement | HTMLOptionElement>(
					':checked',
					inputWrappers[index - 1]
				).value
				const availableOptionInputsValue = selectedOptionOneVariants
					.filter(
						(variant) =>
							variant.available &&
							this.getVariantOptionFromIndex(variant, index) ===
								previousOptionSelected
					)
					.map((variantOption) =>
						this.getVariantOptionFromIndex(variantOption, index + 1)
					)
				this.setInputAvailability(optionInputs, availableOptionInputsValue)
			})
		} else {
			console.warn('no input wrappers found')
		}
	}

	setInputAvailability(listOfOptions:(HTMLInputElement|HTMLOptionElement)[], listOfAvailableOptions:(string|null)[]) {
		listOfOptions.forEach((input) => {
			if (listOfAvailableOptions.includes(input.getAttribute('value'))) {
				input.innerText = q.ra(input, 'value')
			} else {
				input.innerText = window.variantStrings.unavailable_with_option.replace(
					'[value]',
          q.ra(input, 'value')
				)
			}
		})
	}

	async updatePickupAvailability() {
		await customElements.whenDefined('pickup-availability')
		const pickUpAvailability = q.os<PickupAvailability>('pickup-availability')
		if (!pickUpAvailability) return
		const currentVariant = this.currentVariant

		if (currentVariant && currentVariant.available) {
			pickUpAvailability.fetchAvailability(`${currentVariant.id}`)
		} else {
			pickUpAvailability.removeAttribute('available')
			pickUpAvailability.innerHTML = ''
		}
	}

	removeErrorMessage() {
		const section = this.closest('section')
		if (!section) return

		const productForm = q.os<ProductForm>('product-form', section)
		if (productForm) productForm.handleErrorMessage()
	}

	async renderProductInfo(): Promise<void> {
		if (!this.currentVariant)
			throw new Error('this.currentVariant not selected, renderProductInfo called too early')
		const requestedVariantId = this.currentVariant.id
		const sectionId = this.dataset.originalSection
			? this.dataset.originalSection
			: q.ra(this, 'data-section')

		return fetch(
			`${this.dataset.url}?variant=${requestedVariantId}&section_id=${
				this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section
			}`
		)
			.then((response) => {
				if (!response.ok) {
					throw new Error(`Failed to fetch product info: ${response.status}`)
				}
				return response.text()
			})
			.then((responseText) => {
				if (!this.currentVariant)
					throw new Error(
						'this.currentVariant not selected, renderProductInfo called too early'
					)
				// prevent unnecessary ui changes from abandoned selections
				if (this.currentVariant.id !== requestedVariantId) return

				const html = new DOMParser().parseFromString(responseText, 'text/html')
				const destination = document.getElementById(`price-${this.dataset.section}`)
				const source = html.getElementById(
					`price-${
						this.dataset.originalSection
							? this.dataset.originalSection
							: this.dataset.section
					}`
				)
				const skuSource = html.getElementById(
					`Sku-${
						this.dataset.originalSection
							? this.dataset.originalSection
							: this.dataset.section
					}`
				)
				const skuDestination = document.getElementById(`Sku-${this.dataset.section}`)
				const inventorySource = q.os(
					`#Inventory-${
						this.dataset.originalSection
							? this.dataset.originalSection
							: this.dataset.section
					}`,
					html
				)
				const inventoryDestination = document.getElementById(
					`Inventory-${this.dataset.section}`
				)


				if (source && destination) destination.innerHTML = source.innerHTML
				if (inventorySource && inventoryDestination)
					inventoryDestination.innerHTML = inventorySource.innerHTML
				if (skuSource && skuDestination) {
					skuDestination.innerHTML = skuSource.innerHTML
					skuDestination.classList.toggle(
						'visibility-hidden',
						skuSource.classList.contains('visibility-hidden')
					)
				}

				const price = document.getElementById(`price-${this.dataset.section}`)

				if (price) price.classList.remove('visibility-hidden')

				if (inventoryDestination)
					inventoryDestination.classList.toggle(
						'visibility-hidden',
						inventorySource?.innerText === ''
					)

				const addButtonUpdated = html.getElementById(`ProductSubmitButton-${sectionId}`)
				// Button always has 'disabled' attribute in Liquid, so check aria-disabled and data-status instead
				const inventoryStatus = addButtonUpdated?.getAttribute('data-status')
				const ariaDisabled = addButtonUpdated?.getAttribute('aria-disabled')
				// Button should be disabled if: PreOrder (aria-disabled='true'), OutOfStock, ComingSoon, or unavailable
				const shouldBeDisabled = !addButtonUpdated || 
					ariaDisabled === 'true' || 
					inventoryStatus === 'OutOfStock' || 
					inventoryStatus === 'ComingSoon' ||
					!this.currentVariant?.available
				this.toggleAddButton(
					shouldBeDisabled,
					window.variantStrings.soldOut
				)

				const ctaSource = html.getElementById(
					`ProductSubmitButton-${
						this.dataset.originalSection
							? this.dataset.originalSection
							: this.dataset.section
					}`
				)
				const ctaDestination = document.getElementById(`ProductSubmitButton-${this.dataset.section}`)

				if (ctaSource && ctaDestination) {
					ctaDestination.innerHTML = ctaSource.innerHTML
					const sourceDataOosPopupTrigger = q.oa(ctaSource, 'data-oos-popup-trigger')
					const inventoryStatus = q.oa(ctaSource, 'data-status')
					const productSize = q.oa(ctaSource, 'data-size')
					if(productSize) {
						ctaDestination.setAttribute('data-size', productSize)
					} else {
						ctaDestination.removeAttribute('data-size')
					}

					if (sourceDataOosPopupTrigger) {
						ctaDestination.setAttribute('data-oos-popup-trigger', sourceDataOosPopupTrigger)
						ctaDestination.removeAttribute('disabled')
						ctaDestination.removeAttribute('aria-disabled')
					} else {
						ctaDestination.removeAttribute('data-oos-popup-trigger')
					}

					const productId = q.oa(ctaSource, 'data-product-id')
					const variantId = q.oa(ctaSource, 'data-variant-id')

					if (productId && variantId) {
						ctaDestination.setAttribute('data-product-id', productId)
						ctaDestination.setAttribute('data-variant-id', variantId)
					} else {
						ctaDestination.removeAttribute('data-product-id')
						ctaDestination.removeAttribute('data-variant-id')
					}

					ctaDestination.closest('form')?.classList.toggle('coming--soon', inventoryStatus == 'ComingSoon')
				}

				const productform = document.getElementById(`product-form-${this.dataset.section}`)

				if (productform) {
					const globalPreorderBox = html?.querySelector('.preorder-box')
					const formPreorderBox = productform.querySelector('.preorder-box')
					const preorderInput = productform.querySelector('.preorder-box__input')

					if (!formPreorderBox) {
						// If no preorder box, ensure button state is correct based on variant availability
						// toggleAddButton already handled the state, so we don't need to do anything here
						return
					}

					const shouldHide = globalPreorderBox?.classList.contains('hidden')
					formPreorderBox.classList.toggle('hidden', shouldHide)

					if (!shouldHide) {
						const isPreorderActive = preorderInput ? preorderInput.checked : true

						if (ctaDestination) {
							ctaDestination.toggleAttribute('disabled', !isPreorderActive)
							ctaDestination.setAttribute('aria-disabled', String(!isPreorderActive))
						}
					} else {
						// Preorder box is hidden, button state was already set by toggleAddButton above
						// No need to update it again here
					}
				}


				publish(
					PUB_SUB_EVENTS.variantChange,
					createVariantChangeEvent({
						source: 'product-form',
						data: {
							sectionId,
							html,
							variant: this.currentVariant,
							section: q.ra(this, 'data-section')
						},
					})
				)
			})
			.catch((error) => {
				console.error('Error in renderProductInfo promise chain:', error)
				throw error
			})
	}

	toggleAddButton(disable = true, text: string, modifyClass = true) {
		const productForm = q.rs<HTMLFormElement>(`#product-form-${this.dataset.section}`)
		if (!productForm) return
		const addButton = q.os('[name="add"]', productForm)
		const addButtonText = q.os('[name="add"] > span', productForm)
		if (!addButton || !addButtonText) return

		if (disable) {
			addButton.setAttribute('disabled', 'disabled')
			addButton.setAttribute('aria-disabled', 'true')
			if (text) addButtonText.textContent = text
		} else {
			addButton.removeAttribute('disabled')
			addButton.removeAttribute('aria-disabled')
			addButtonText.textContent = window.variantStrings.addToCart
		}

		if(addButton.hasAttribute('data-oos-popup-trigger')) {
			addButton.removeAttribute('disabled')
			addButton.removeAttribute('aria-disabled')
		}

		if (!modifyClass) return
	}

	setUnavailable() {
		const button = q.rs(`#product-form-${this.dataset.section}`)
		const addButton = q.os('[name="add"]', button)
		const addButtonText = q.os('[name="add"] > span', button)
		const price = document.getElementById(`price-${this.dataset.section}`)
		const inventory = document.getElementById(`Inventory-${this.dataset.section}`)
		const sku = document.getElementById(`Sku-${this.dataset.section}`)

		if (!addButton || !addButtonText) return
		addButtonText.textContent = window.variantStrings.unavailable
		if (price) price.classList.add('visibility-hidden')
		if (inventory) inventory.classList.add('visibility-hidden')
		if (sku) sku.classList.add('visibility-hidden')
	}

	getVariantData(): ProductVariant[] {
		const textContent = q.rs<HTMLScriptElement>(
			'[type="application/json"]',
			this
		).textContent
		if (!textContent) throw new Error('Variant data not found')
		const updatedVariantData = this.variantData ?? JSON.parse(textContent)
		this.variantData = updatedVariantData
		return updatedVariantData
	}

	async onClickSwatch(event: Event) {
		event.preventDefault()
		const target = q.rClosestTarget(event, '.swatch-link') as HTMLLinkElement
		if (!target) return

		const originalElement = target.closest('[id^="QuickAddInfo-"]') as HTMLElement || target.closest('section[id^="MainProduct-"]') as HTMLElement
		if (target.closest('build-set-drawer') || !originalElement) return

		try {
			// Set loading state
			target.setAttribute(ATTRIBUTES.loading, '')
			const spinner = q.rs(SELECTORS.loadingOverlaySpinner, target)
			if (spinner) spinner.classList.remove('hidden')

			const productUrl = q.ra(target, 'data-product-url')
			if (!productUrl) throw new Error('No product URL found')

			// Handle product page updates
			if (document.body.classList.contains('template--product')) {
				await updateOtherPages(productUrl, originalElement, this)
			} else {
				await updateProductPage(productUrl, originalElement, this)
			}
		} catch (error) {
			console.error('Error loading sections:', error)
		} finally {
			// Clean up loading state
			target.removeAttribute(ATTRIBUTES.loading)
			const spinner = q.rs(SELECTORS.loadingOverlaySpinner, target)
			if (spinner) spinner.classList.add('hidden')
		}
	}

	onClickSwatchTab(even: Event) {
		even.preventDefault()
		const target = even.target as HTMLButtonElement
		if(!target) return
		const category = target.dataset.category
		if(category) {
			q.ol('.color-swatch-tabs__body .swatch-link', document)?.forEach(swatch => {
				swatch.classList.toggle('hidden', category != swatch.dataset.category && category != 'all')
			})
			q.ol('.color-swatch-tab', document)?.forEach(tab => {
				tab.classList.toggle('active', category == tab.dataset.category)
			})
		}
	}

	setInnerHTML(element: HTMLElement, html: string) {
		element.innerHTML = html

		// Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
		element.querySelectorAll('script').forEach((oldScriptTag: HTMLScriptElement) => {
			if (!oldScriptTag.parentNode)
				throw new Error('oldScriptTag.parentNode is null, cannot replace script')
			const newScriptTag = document.createElement('script')
			Array.from(oldScriptTag.attributes).forEach((attribute) => {
				newScriptTag.setAttribute(attribute.name, attribute.value)
			})
			newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML))
			oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag)
		})
	}

	preventVariantURLSwitching(originalElement: HTMLElement) {
		const variantPicker = originalElement.querySelector('variant-radios,variant-selects')
		if (!variantPicker) return

		variantPicker.setAttribute('data-update-url', 'false')
	}

	removeDOMElements(productElement: HTMLElement) {
		if (!productElement)
			throw new Error('this.removeDOMElements called without productElement')
		q.os<PickupAvailability>('pickup-availability', productElement)?.remove()
		q.os<ProductModal>('product-modal', productElement)?.remove()
		const modalDialogs = q.ol<ModalDialog>('modal-dialog', productElement)
		if (modalDialogs) {
			modalDialogs.forEach((modal) => modal.remove())
		}
	}

	preventDuplicatedIDs(productElement: HTMLElement) {
		if (!productElement)
			throw new Error('this.preventDuplicatedIDs called without productElement')
		const sectionId = q.ra(productElement, 'data-section')
		productElement.innerHTML = replaceAll(
			productElement.innerHTML,
			sectionId,
			`quickadd-${sectionId}`
		)
		const formComponents = q.ol<VariantSelects | VariantRadios | ProductInfo>(
			'variant-selects, variant-radios, product-info',
			productElement
		)
		formComponents?.forEach((element) => {
			element.dataset.originalSection = sectionId
		})
	}

	removeGalleryListSemantic(originalElement: HTMLElement) {
		const galleryList = originalElement.querySelector('[id^="Slider-Gallery"]')
		if (!galleryList) return

		if(originalElement.classList.contains('quick-add-modal__content-info')) galleryList.classList.remove('slider')
		galleryList.setAttribute('role', 'presentation')
		galleryList
			.querySelectorAll('[id^="Slide-"]')
			.forEach((li) => li.setAttribute('role', 'presentation'))
	}

	updateImageSizes(originalElement: HTMLElement) {
		const product = q.rs('.product', originalElement)
		const desktopColumns = product.classList.contains('product--columns')
		if (!desktopColumns) return

		const mediaImages = q.ol<HTMLImageElement>('.product__media img', product)
		if (!mediaImages) return

		let mediaImageSizes =
			'(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)'

		if (product.classList.contains('product--medium')) {
			mediaImageSizes = mediaImageSizes.replace('715px', '605px')
		} else if (product.classList.contains('product--small')) {
			mediaImageSizes = mediaImageSizes.replace('715px', '495px')
		}

		mediaImages.forEach((img: HTMLImageElement) => img.setAttribute('sizes', mediaImageSizes))
	}

	async onChangeColorDropdown(event: Event) {
		event.preventDefault()
        const target = q.rt<Event, HTMLSelectElement>(event)
		const option = target.options[target.options.selectedIndex] as HTMLOptionElement
		const container = target.closest('.product-upsell-card') as HTMLElement
        if(!option || !container) return
		const response = await fetch(`/products/${option.dataset.handle}?section_id=product-upsell-card`, {
            credentials: 'same-origin',
            method: 'GET',
        })
        const responseText = await response.text()
        const card = q.rs('.product-upsell-card', new DOMParser().parseFromString(responseText, 'text/html'))
		card.querySelector('variant-radios').dataset.originalSection = q.rs('section[id^="MainProduct-"]', document).getAttribute('id')?.replace('MainProduct-', '')
        container.outerHTML = card.outerHTML
	}
}

// Helper functions
async function updateProductPage(productUrl: string, originalElement: HTMLElement, _this: VariantSelects) {
	const response = await fetch(`${productUrl}?section_id=${MAIN_PRODUCT_SECTION_ID}`)
	if (!response.ok) throw new Error(`Failed to fetch product: ${response.status}`)

	const responseText = await response.text()
	const responseHTML = new DOMParser().parseFromString(responseText, 'text/html')
	const productElement = q.rs('section[id^="MainProduct-"]', responseHTML.documentElement)

	if (!productElement) throw new Error('Product section not found')

	await updateProductContent(productElement, originalElement, productUrl, _this, true)
}

async function updateOtherPages(productUrl: string, originalElement: HTMLElement, _this: VariantSelects) {
	const sections = document.querySelectorAll('.content-for-layout section.shopify-section')
	const updatePromises = Array.from(sections).map(async (section) => {
		if (!section.querySelector('section[id^="MainProduct-"]') &&
			originalElement.classList.contains('quick-add-modal__content-info')) return

		const sectionId = section.getAttribute('id')?.replace('shopify-section-', '')
		if (!sectionId) return

		const response = await fetch(`${productUrl}?section_id=${sectionId}`)
		if (!response.ok) return

		const responseText = await response.text()
		const responseHTML = new DOMParser().parseFromString(responseText, 'text/html')

		if (section.querySelector('section[id^="MainProduct-"]')) {
			const productElement = q.rs('section[id^="MainProduct-"]', responseHTML.documentElement)
			if (productElement) {
				await updateProductContent(productElement, originalElement, productUrl, _this, false)
			}
		} else {
			const html = q.rs('section.shopify-section', responseHTML.documentElement)
			if (html) {
				_this.setInnerHTML(section as HTMLElement, html.innerHTML)
			}
		}
	})

	// Wait for all section updates to complete
	await Promise.all(updatePromises)
}

async function updateProductContent(productElement: HTMLElement, originalElement: HTMLElement, productUrl: string, _this: VariantSelects, removeDom: boolean) {
	console.log(removeDom)
	_this.preventDuplicatedIDs(productElement)
	if(removeDom) _this.removeDOMElements(productElement)
	_this.setInnerHTML(originalElement, productElement.innerHTML)

	// Initialize media and features
	await Promise.all([
		window.Ucoast.mediaManager.playAllInContainer(originalElement),
		initializeProductFeatures()
	])

	_this.removeGalleryListSemantic(originalElement)
	_this.updateImageSizes(originalElement)
	_this.preventVariantURLSwitching(originalElement)

	if (!originalElement.classList.contains('quick-add-modal__content-info')) {
		window.history.replaceState({}, '', productUrl)
	}

	const productModals = document.querySelectorAll('product-modal')
	productModals.forEach((modal, index) => {
		if(index < productModals.length - 1) modal.remove()
	})
}

async function initializeProductFeatures() {
	if (window.Shopify?.PaymentButton) {
		window.Shopify.PaymentButton.init()
	}
	if (window.ProductModel) {
		window.ProductModel.loadShopifyXR()
	}
}
